/**
 * `dm.job` — the lane runtime's queue record (DM 2126 S1a).
 *
 * A RUNTIME row, not a content one: spec 637's hierarchy (project → development →
 * epic → item) does not contain it, and a job points AT the item it is work for
 * rather than standing in for it.
 *
 * Here rather than in `packages/api` because `packages/web` renders the queue (S3)
 * and cannot import from the api package — the same reason `dmLifecycleState.ts`
 * and `PmAgentRunStatus` live here.
 */
import { z } from 'zod';
/** The type key, spelled once for every package that files or reads a job. */
export const DM_JOB_TYPE = 'dm.job';
/**
 * The job graph's state names, in graph order.
 *
 * `blocked` and `done` are already in `DM_LIFECYCLE_STATE_VALUES` (the dm package
 * shares words across types); the other five arrive with this type.
 */
export const DM_JOB_STATUS_VALUES = [
    'queued',
    'claimed',
    'running',
    'failed',
    'blocked',
    'done',
    'cancelled',
];
/** The seeded lifecycle's `terminal: true` states. */
export const DM_JOB_TERMINAL_STATUSES = new Set([
    'done',
    'cancelled',
]);
/** Source states of the seeded `→ cancelled` edges (`blocked`: migration 0043). */
export const DM_JOB_CANCELLABLE_STATUSES = new Set([
    'queued',
    'claimed',
    'blocked',
]);
/** What `requeueDmJob` puts back on the queue (`failed` never commits — `failDmJob` moves on). */
export const DM_JOB_REQUEUEABLE_STATUSES = new Set(['blocked']);
/**
 * How many dispatch attempts a job gets before it stops being retried (K5).
 *
 * The SECOND failure blocks it; a human re-queues. Automation retrying past this is
 * how a poison job burns a lane indefinitely.
 */
export const DM_JOB_MAX_ATTEMPTS = 2;
/**
 * The name the ATTEMPT CEILING's refusal carries (DM 2126 S1a).
 *
 * A gate key, but NOT one any `pm_type_def` holds: `queued` is the born state and rule 5c
 * refuses an entry gate there (creation traverses no edge, so the gate would read as
 * enforcement while never running). The rule is declared as a BORN-STATE RE-ENTRY gate
 * instead — `PM_BORN_STATE_REENTRY_GATES` in `pmLifecycle.ts` — and evaluated by the same
 * engine, through the same predicate registry. Here in `@elmes/shared` because the pm
 * engine and the dm seed's prose both name it and two spellings would drift.
 */
export const DM_JOB_RETRY_BUDGET_GATE = 'retry-budget';
/** Lane names are `dm-lane-<n>`: the tmux session, the systemd instance and this string are one. */
export const DM_LANE_NAME_RE = /^dm-lane-[1-9][0-9]{0,2}$/;
export const dmLaneNameSchema = z
    .string()
    .regex(DM_LANE_NAME_RE, 'lane names are dm-lane-<n> (n = 1..999)');
/** W6-4 (K3) — what a job is: lane work, or a server-side ship. Absent = agent. */
export const DM_JOB_KIND_VALUES = ['agent', 'ship'];
export const DM_JOB_KIND_DEFAULT = 'agent';
/** The ship worker's holder stamp — never a lane name, so no lane door can name it. */
export const DM_SHIP_WORKER_CLAIMANT = 'dm-ship-worker';
/** Who may hold a job: a lane, or the ship worker. Service-side only; the lane doors keep {@link dmLaneNameSchema}. */
export const dmJobClaimantSchema = z.union([dmLaneNameSchema, z.literal(DM_SHIP_WORKER_CLAIMANT)]);
/** A ship job's smoke list and one path's bound. */
export const DM_SHIP_SMOKE_PATHS_MAX = 10;
export const DM_SHIP_PATH_MAX_CHARS = 200;
/** Stored form: the paths joined by `\n` (the scalar data dialect has no array). */
export const DM_SHIP_SMOKE_PATHS_STORED_MAX_CHARS = DM_SHIP_SMOKE_PATHS_MAX * DM_SHIP_PATH_MAX_CHARS + (DM_SHIP_SMOKE_PATHS_MAX - 1);
/** Loopback request path: one leading `/`, never `//`, no scheme, no backslash, no whitespace/controls. */
export const DM_SHIP_PATH_RE = /^\/(?!\/)[\x21-\x5b\x5d-\x7e]*$/;
export const dmShipPathSchema = z
    .string()
    .max(DM_SHIP_PATH_MAX_CHARS)
    .regex(DM_SHIP_PATH_RE, 'a path starts with one "/" and holds printable ASCII only (no "//", no backslash)');
/** A ship enqueue's payload, as every door takes it. */
export const dmShipJobPayloadSchema = z
    .object({
    pr: z.number().int().positive(),
    smokePaths: z.array(dmShipPathSchema).max(DM_SHIP_SMOKE_PATHS_MAX).optional(),
})
    .strict();
/** `smoke_paths` as stored → the list; a stored value this cannot read is null (fail-closed at the reader). */
export function decodeDmShipSmokePaths(stored) {
    if (stored === undefined)
        return [];
    if (typeof stored !== 'string')
        return null;
    if (stored === '')
        return [];
    const paths = stored.split('\n');
    if (paths.length > DM_SHIP_SMOKE_PATHS_MAX)
        return null;
    return paths.every((path) => dmShipPathSchema.safeParse(path).success) ? paths : null;
}
/** The list → its stored form; the caller has validated each path. */
export function encodeDmShipSmokePaths(paths) {
    return paths.join('\n');
}
/** W6-4 — a ship job's steps, in order. */
export const DM_SHIP_STEP_VALUES = ['probe', 'gate', 'ci-wait', 'merge', 'deploy-wait', 'verify'];
/** W6-4 — what one `ship.step` event says of its step. */
export const DM_SHIP_STEP_OUTCOME_VALUES = ['passed', 'failed', 'blocked', 'skipped', 'aborted'];
/** A `ship.step` event's `detail` ceiling — the worker clips to it and the event door publishes no more. */
export const DM_SHIP_STEP_DETAIL_MAX_CHARS = 500;
/** Why a ship claim was handed back without a spent attempt. */
export const DM_SHIP_PAUSE_REASON_VALUES = ['worker_shutdown', 'stale_heartbeat'];
/** Where a paused ship job picks up: from scratch, or past its recorded merge (c6318 B2). */
export const DM_SHIP_RESUME_FROM_VALUES = ['probe', 'deploy-wait'];
/** c6321 — pauses (shutdown or reaper) a ship job takes before the next one blocks it `resume_cap`; only a human requeue resets the count. */
export const DM_SHIP_MAX_RESUMES = 5;
/** A full git commit sha, lower-case. */
export const DM_GIT_SHA_RE = /^[0-9a-f]{40}$/;
export const dmGitShaSchema = z.string().regex(DM_GIT_SHA_RE, 'a full 40-hex lower-case commit sha');
/** Longest `result` / `last_error` a job may carry — a receipt, never a transcript. */
export const DM_JOB_TEXT_MAX_CHARS = 2000;
const isoTimestamp = z.string().datetime({ offset: true });
/**
 * The `pm_item.data` contract for a job, FLAT and snake_case.
 *
 * Flat because `pm_type_def.data_schema` declares scalar fields only (`PM_DATA_FIELD_TYPES`).
 * W8-1b: merge authority left the bag — it is a `pm_grant` row (0040).
 */
export const dmJobDataSchema = z
    .object({
    /** The dm item/issue this job is work FOR. The `references` edge is canonical; this is the read key. */
    target_item_id: z.number().int().positive(),
    /** `null`/absent = any lane may claim it. A name pins it to that lane. */
    lane: dmLaneNameSchema.nullable().optional(),
    /** W6-4 — routing: absent = agent. Written only by a ship enqueue. */
    kind: z.enum(DM_JOB_KIND_VALUES).optional(),
    /** W6-4 — the PR a ship job merges (its merge authority is this PR's only). */
    pr: z.number().int().positive().optional(),
    /** W6-4 — `\n`-joined smoke paths ({@link decodeDmShipSmokePaths}). */
    smoke_paths: z.string().max(DM_SHIP_SMOKE_PATHS_STORED_MAX_CHARS).optional(),
    /** W6-4 (c6318 B2) — the ship worker's progress: the step in hand, the merge it made, the deploy run it saw. */
    ship_step: z.enum(DM_SHIP_STEP_VALUES).optional(),
    merge_sha: dmGitShaSchema.optional(),
    deploy_run_id: z.number().int().positive().optional(),
    /** W6-4 (c6318 B2) — set by a pause, consumed by the next ship claim. */
    resume_from: z.enum(DM_SHIP_RESUME_FROM_VALUES).optional(),
    /** c6321 — pauses taken so far ({@link DM_SHIP_MAX_RESUMES}); absent = none. */
    resume_count: z.number().int().nonnegative().optional(),
    attempts: z.number().int().nonnegative().optional(),
    /** The holder — a lane name or the ship worker, not a principal. */
    claimed_by: dmJobClaimantSchema.optional(),
    claimed_at: isoTimestamp.optional(),
    heartbeat_at: isoTimestamp.optional(),
    /** `pm_agent_run.id` the lane started it under; the server resolves it, callers do not send it. */
    agent_run_id: z.number().int().positive().optional(),
    result: z.string().max(DM_JOB_TEXT_MAX_CHARS).optional(),
    last_error: z.string().max(DM_JOB_TEXT_MAX_CHARS).optional(),
})
    .strict();
/**
 * Data keys a WRITE may set only through a door that verified a human (K6).
 *
 * Keyed by type so the kernel's generic write seam can enforce it without knowing
 * what a job is. W8-1b: the two merge keys are RETIRED (authority is a `pm_grant` row) and
 * stay reserved, so a stale writer is refused rather than stored unread.
 */
export const DM_HUMAN_ONLY_DATA_KEYS = {
    [DM_JOB_TYPE]: ['autonomy_merge', 'autonomy_merge_sha'],
};
/**
 * Every human-only data key, across every type — the RESERVED set.
 *
 * The kernel's generic create/update seams refuse the whole set without asking what
 * type the row is: they have no caller-origin to test (each states its own, and these
 * two do not), so the only fail-CLOSED reading available there is "not through this
 * door, for anyone". The dedicated doors that CAN state an origin write the key
 * themselves, which is what keeps the refusal from being a ban.
 */
export const DM_RESERVED_HUMAN_ONLY_DATA_KEYS = [
    ...new Set(Object.values(DM_HUMAN_ONLY_DATA_KEYS).flat()),
];
/**
 * Data keys only the dm.job SERVICE writes — the queue's own bookkeeping (S1a review D1).
 *
 * `attempts` is the retry ceiling's counter, and since the ceiling became a GATE
 * (`DM_JOB_RETRY_BUDGET_GATE`) it is the INPUT to an authority decision: a door that can
 * set it to zero disarms the ceiling, and a door that can REMOVE it strands the job —
 * the predicate refuses a counter it cannot read, so the retry the failure path chose is
 * then denied and the row never leaves the lane's hands. The other four are the holder
 * stamp and the run pointer, i.e. the facts the ownership check and the lane readers
 * answer from; naming yourself the holder is taking another lane's work.
 *
 * Distinct from {@link DM_RESERVED_HUMAN_ONLY_DATA_KEYS} in WHO is excluded, not in how:
 * these are refused to every door but the one service that owns them, and that service
 * writes them through its own statement rather than through the generic seam. The
 * refusal therefore needs the TYPE (these are `dm.job` field names, not reserved words),
 * which is why it is asked at the type-aware seam.
 */
export const DM_JOB_SERVICE_OWNED_DATA_KEYS = [
    'attempts',
    'claimed_by',
    'claimed_at',
    'heartbeat_at',
    'agent_run_id',
];
/** W6-4 (c6318 B2) — the ship worker's progress and resume stamps: written by the ship arm only, frozen at both seams. */
export const DM_JOB_SHIP_PROGRESS_DATA_KEYS = [
    'ship_step',
    'merge_sha',
    'deploy_run_id',
    'resume_from',
    'resume_count',
];
/** The lane routing keys — authored at CREATE (the enqueue door writes them there), frozen at UPDATE (S1a review E1). */
export const DM_JOB_LANE_ROUTING_DATA_KEYS = ['lane', 'target_item_id'];
export const DM_JOB_LANE_ROUTING_DATA_KEYS_TEXT = DM_JOB_LANE_ROUTING_DATA_KEYS.join(' / ');
/**
 * W6-4 (c6318 B5) — a ship job's routing: what the job does and which PR a merge grant applies to. Frozen at
 * BOTH seams: only the enqueue door writes them (after its payload refinement), so no generic create mints one.
 */
export const DM_JOB_SHIP_ROUTING_DATA_KEYS = ['kind', 'pr', 'smoke_paths'];
export const DM_JOB_SHIP_ROUTING_DATA_KEYS_TEXT = DM_JOB_SHIP_ROUTING_DATA_KEYS.join(' / ');
/**
 * The queue's ROUTING keys — every one frozen at the UPDATE seam (S1a review E1).
 *
 * What a PATCH of them changes is where the job goes: the claim filter reads `data->>'lane'`, so re-pinning
 * moves the job to a lane that never asked for it (or, with a name no lane runs, out of reach entirely),
 * `target_item_id` is the read key every reader answers from while the `references` edge stays canonical
 * (repointing it splits the two), and re-pointing a ship job's kind or PR moves its merge grant.
 */
export const DM_JOB_ROUTING_DATA_KEYS = [
    ...DM_JOB_LANE_ROUTING_DATA_KEYS,
    ...DM_JOB_SHIP_ROUTING_DATA_KEYS,
];
/** The routing keys as a sentence, for the prose that must not respell them. */
export const DM_JOB_ROUTING_DATA_KEYS_TEXT = DM_JOB_ROUTING_DATA_KEYS.join(' / ');
/** What the generic CREATE seam freezes: the service's bookkeeping, the ship progress and the ship routing. */
export const DM_JOB_CREATE_FROZEN_DATA_KEYS = [
    ...DM_JOB_SERVICE_OWNED_DATA_KEYS,
    ...DM_JOB_SHIP_PROGRESS_DATA_KEYS,
    ...DM_JOB_SHIP_ROUTING_DATA_KEYS,
];
/** The create-seam set as a sentence, for the tool descriptions that must not respell it. */
export const DM_JOB_CREATE_FROZEN_DATA_KEYS_TEXT = DM_JOB_CREATE_FROZEN_DATA_KEYS.join(' / ');
/** What the generic UPDATE seam freezes: the service's bookkeeping, the ship progress and every routing key. */
export const DM_JOB_UPDATE_FROZEN_DATA_KEYS = [
    ...DM_JOB_SERVICE_OWNED_DATA_KEYS,
    ...DM_JOB_SHIP_PROGRESS_DATA_KEYS,
    ...DM_JOB_ROUTING_DATA_KEYS,
];
/** The update-seam set as a sentence, for the tool descriptions that must not respell it. */
export const DM_JOB_UPDATE_FROZEN_DATA_KEYS_TEXT = DM_JOB_UPDATE_FROZEN_DATA_KEYS.join(' / ');
/** Which keys each generic seam freezes — the seam difference, stated once. */
export const DM_JOB_FROZEN_DATA_KEYS_BY_SEAM = {
    create: DM_JOB_CREATE_FROZEN_DATA_KEYS,
    update: DM_JOB_UPDATE_FROZEN_DATA_KEYS,
};
/**
 * Which keys of `keys` a patch NAMES — set or removed.
 *
 * ONE predicate shape for every key set and for both of its readers (the caller that
 * decides to check, the guard that refuses), with the same arguments: removal counts as
 * naming, because clearing a key is a write to it
 * ([[feedback_derived_signal_survives_ctx_rebuild]]).
 */
function dmDataKeysNamed(keys, authored, unset) {
    return keys.filter((key) => authored[key] !== undefined || unset.includes(key));
}
/** Which reserved human-only keys a patch NAMES — set or removed. */
export function dmReservedHumanOnlyDataKeysNamed(authored, unset = []) {
    return dmDataKeysNamed(DM_RESERVED_HUMAN_ONLY_DATA_KEYS, authored, unset);
}
/** Which `dm.job` keys frozen AT THIS SEAM a patch NAMES — set or removed. */
export function dmJobFrozenDataKeysNamed(seam, authored, unset = []) {
    return dmDataKeysNamed(DM_JOB_FROZEN_DATA_KEYS_BY_SEAM[seam], authored, unset);
}
//# sourceMappingURL=dmJob.js.map